<script setup lang="ts">
/**
 * 评价轮播 — Inspira UI
 * 文档：https://inspira-ui.com/docs/en/components/testimonials/testimonial-slider
 */
import { nextTick, onMounted, onUnmounted, ref, watch } from 'vue';

interface Testimonial {
  img: string;
  quote: string;
  name: string;
  role: string;
}

const props = withDefaults(
  defineProps<{
    testimonials?: Testimonial[];
    autoRotate?: boolean;
    duration?: number;
  }>(),
  {
    autoRotate: true,
    duration: 5,
    testimonials: () => [],
  },
);

const active = ref(0);
/** 1 = 向右切（下一张），-1 = 向左切（上一张） */
const direction = ref<1 | -1>(1);
const autorotate = ref(props.autoRotate);
const inView = ref(false);
const rootRef = ref<HTMLElement | null>(null);
const testimonialsRef = ref<HTMLElement | null>(null);
let intervalId: number | null = null;
let visibilityObserver: IntersectionObserver | null = null;

function heightFix() {
  if (testimonialsRef.value?.parentElement) {
    testimonialsRef.value.parentElement.style.height = `${testimonialsRef.value.clientHeight}px`;
  }
}

function setActiveIndex(index: number) {
  active.value = index;
  autorotate.value = false;
  resetAutorotate();
  nextTick(heightFix);
}

function startAutorotate() {
  if (intervalId) {
    clearInterval(intervalId);
    intervalId = null;
  }
  if (!autorotate.value || !inView.value || document.hidden) return;
  intervalId = window.setInterval(() => {
    direction.value = 1;
    active.value = active.value + 1 === props.testimonials.length ? 0 : active.value + 1;
    heightFix();
  }, props.duration * 1000);
}

function resetAutorotate() {
  if (intervalId) {
    clearInterval(intervalId);
    intervalId = null;
  }
  if (autorotate.value) {
    startAutorotate();
  }
}

function handleNext() {
  direction.value = 1;
  setActiveIndex((active.value + 1) % props.testimonials.length);
}

function handlePrev() {
  direction.value = -1;
  setActiveIndex((active.value - 1 + props.testimonials.length) % props.testimonials.length);
}

function onVisibilityChange() {
  if (document.hidden) {
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
    return;
  }
  if (autorotate.value) startAutorotate();
}

watch(active, () => {
  nextTick(heightFix);
});

onMounted(() => {
  heightFix();

  visibilityObserver = new IntersectionObserver(
    ([entry]) => {
      inView.value = entry?.isIntersecting ?? false;
      if (inView.value) {
        if (autorotate.value) startAutorotate();
        return;
      }
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    },
    { threshold: 0.2 },
  );
  if (rootRef.value) visibilityObserver.observe(rootRef.value);

  document.addEventListener('visibilitychange', onVisibilityChange);
});

onUnmounted(() => {
  if (intervalId) clearInterval(intervalId);
  visibilityObserver?.disconnect();
  visibilityObserver = null;
  document.removeEventListener('visibilitychange', onVisibilityChange);
});
</script>

<template>
  <div
    ref="rootRef"
    class="mx-auto w-full max-w-3xl text-center"
    :class="direction === 1 ? 'is-next' : 'is-prev'"
  >
    <div class="relative h-24 md:h-32">
      <div
        class="pointer-events-none absolute top-0 left-1/2 size-[300px] -translate-x-1/2 before:absolute before:inset-0 before:-z-10 before:rounded-full before:bg-linear-to-b before:from-primary/20 before:via-primary/5 before:via-25% before:to-primary/0 before:to-75% md:size-[480px]"
      >
        <div class="h-24 mask-[linear-gradient(0deg,transparent,white_20%,white)] md:h-32">
          <TransitionGroup name="testimonial-image">
            <div
              v-for="(testimonial, index) in testimonials"
              v-show="active === index"
              :key="`image-${index}`"
              class="absolute inset-0 -z-10 flex h-full flex-col"
            >
              <img
                class="relative top-7 left-1/2 size-12 -translate-x-1/2 rounded-full object-cover ring-2 ring-white shadow-lg md:top-11 md:size-[60px]"
                :src="testimonial.img"
                width="60"
                height="60"
                :alt="testimonial.name"
                loading="lazy"
              />
            </div>
          </TransitionGroup>
        </div>
      </div>
    </div>

    <div class="mb-5 transition-all delay-300 duration-150 ease-in-out md:mb-8">
      <div ref="testimonialsRef" class="relative flex flex-col">
        <TransitionGroup name="testimonial-text">
          <div
            v-for="(testimonial, index) in testimonials"
            v-show="active === index"
            :key="`text-${index}`"
            class="w-full"
          >
            <div
              class="px-1 text-[15px] font-bold leading-7 text-foreground before:content-['\201C'] after:content-['\201D'] md:px-0 md:text-2xl md:leading-normal"
            >
              {{ testimonial.quote }}
            </div>
          </div>
        </TransitionGroup>
      </div>
    </div>

    <div class="mt-2 flex w-full items-center justify-between gap-3 pt-4 md:mt-4 md:gap-4 md:pt-0">
      <button
        type="button"
        class="group/button flex size-8 items-center justify-center rounded-full border border-border bg-background transition hover:bg-muted md:size-9"
        :aria-label="'Previous testimonial'"
        @click="handlePrev"
      >
        <svg
          class="size-4 text-foreground transition-transform duration-300 group-hover/button:-translate-x-0.5 md:size-5"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          aria-hidden="true"
        >
          <path d="M19 12H5M12 19l-7-7 7-7" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </button>

      <div class="flex min-w-0 flex-col items-center gap-0.5 md:gap-1">
        <span class="truncate text-sm font-semibold not-italic text-foreground md:text-base">{{ testimonials.at(active)?.name }}</span>
        <span class="truncate text-xs text-muted-foreground md:text-sm">{{ testimonials.at(active)?.role }}</span>
      </div>

      <button
        type="button"
        class="group/button flex size-8 items-center justify-center rounded-full border border-border bg-background transition hover:bg-muted md:size-9"
        :aria-label="'Next testimonial'"
        @click="handleNext"
      >
        <svg
          class="size-4 text-foreground transition-transform duration-300 group-hover/button:translate-x-0.5 md:size-5"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          aria-hidden="true"
        >
          <path d="M5 12h14M12 5l7 7-7 7" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </button>
    </div>
  </div>
</template>

<style scoped>
.testimonial-image-enter-active,
.testimonial-image-leave-active {
  transition: all 700ms cubic-bezier(0.68, -0.3, 0.32, 1);
}

.testimonial-image-enter-to,
.testimonial-image-leave-from {
  opacity: 1;
  transform: rotate(0deg);
}

/* 下一张：从左旋入，向右旋出 */
.is-next .testimonial-image-enter-from {
  opacity: 0;
  transform: rotate(-60deg);
}

.is-next .testimonial-image-leave-to {
  opacity: 0;
  transform: rotate(60deg);
}

/* 上一张：从右旋入，向左旋出 */
.is-prev .testimonial-image-enter-from {
  opacity: 0;
  transform: rotate(60deg);
}

.is-prev .testimonial-image-leave-to {
  opacity: 0;
  transform: rotate(-60deg);
}

.testimonial-text-enter-active {
  transition: all 500ms ease-in-out 200ms;
}

.testimonial-text-leave-active {
  position: absolute;
  transition: all 300ms ease-out 300ms;
}

.testimonial-text-enter-to,
.testimonial-text-leave-from {
  opacity: 1;
  transform: translateX(0);
}

.is-next .testimonial-text-enter-from {
  opacity: 0;
  transform: translateX(-1rem);
}

.is-next .testimonial-text-leave-to {
  opacity: 0;
  transform: translateX(1rem);
}

.is-prev .testimonial-text-enter-from {
  opacity: 0;
  transform: translateX(1rem);
}

.is-prev .testimonial-text-leave-to {
  opacity: 0;
  transform: translateX(-1rem);
}
</style>
